import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Dialog,
  DialogActions,
  Button,
  TextField,
  SelectChangeEvent,
  TableCell,
  Snackbar,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import '../styles/DatePicker.css';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import config from '../config/environment';

// Helper function to convert seconds to hh:mm format
const formatTimeToHHMM = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  // Format as hh:mm with leading zeros
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  
  return `${formattedHours}:${formattedMinutes}`;
};

interface Track {
  id: string;
  name: string;
  is_active: boolean;
  price: number;
  is_free: boolean;
  track_url?: string;
  created_at?: string;
  updated_at?: string;
  image?: string;
  product_id?: number;
}

const MainContent = styled(Box)({
  padding: '0px 24px',
  backgroundColor: '#ffffff',
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  boxSizing: 'border-box',
  '@media (max-width: 1024px)': {
    padding: '0px 16px',
  }
});

// Styled components
const PopularCard = styled(Card)({
  height: '260px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.02)',
  },
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  width: '100%',
  maxWidth: '340px',
  margin: '0 auto',
  '@media (min-width: 768px)': {
    height: 'auto',
    maxWidth: '400px',
    width: '100%',
    margin: '0 auto',
  },
  '@media (max-width: 768px)': {
    height: 'auto',
    maxWidth: '98vw',
  }
});

const StatsCard = styled(Paper)({
  padding: '20px',
  background: '#ffd999',
  color: 'black',
  borderRadius: '8px',
  height: '100%',
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  minWidth: 0,
  '@media (max-width: 1024px)': {
    padding: '16px',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
  }
});

const CardImage = styled('img')({
  width: '200px',
  height: '200px',
  objectFit: 'cover',
  backgroundColor: '#f5f5f5',
  transition: 'opacity 0.3s ease-in-out',
  padding: '8px',
  backgroundPosition: 'center 50%',
  display: 'block',
  margin: '0 auto',
  '@media (max-width: 1024px)': {
    width: '180px',
    height: '180px',
  },
  '@media (max-width: 768px)': {
    width: '140px',
    height: '140px',
  }
});

const ImagePlaceholder = styled(Box)({
  width: '100%',
  height: '140px',
  backgroundColor: '#f5f5f5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px',
});

const EmptyState = styled(Box)({
  textAlign: 'center',
  padding: '40px',
  background: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '8px',
  marginTop: '20px',
});

const MeditationHours = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  background: '#f5f5f5',
  borderRadius: '12px',
  padding: '2px 8px',
  fontSize: '13px',
  color: '#FF7043',
  minWidth: '70px',
  justifyContent: 'center',
  '@media (max-width: 1024px)': {
    fontSize: '14px',
    padding: '4px 12px',
    minWidth: '80px',
  },
  '@media (max-width: 768px)': {
    fontSize: '15px',
    minWidth: '90px',
  }
});

// Base Firebase Storage URL
const FIREBASE_STORAGE_URL = config.storage.baseURL;

// Map of card names to their corresponding image numbers
const IMAGE_NUMBER_MAP: { [key: string]: string } = {
  'NO-MIND': '1.NO-MIND',
  'Thought': '1-Thought',
  'WATCHFULNESS': '2-WATCHFULNESS-',
  'transcending-ego': '2-transcending-ego',
  'Nothingness': '3-Nothingness',
  'No-thing-ness': '3-Nothingness',
  'Surrender': '4-Surrender',
  'Harmony': '5-Harmony',
  'Infinite-Consciousness': '6-Infinite-Consciousness'
};

interface FirestoreCard {
  id: string;
  name: string;
  image_url: string;
  thumbnail_url?: string;
  description: string;
  category: string;
  is_free: boolean;
  price: number;
  audio_url?: string;
  background_music_url?: string;
  is_active: boolean;
}

interface YantraCard {
  id: string;
  name: string;
  image_url: string;
  thumbnail_url?: string;
  description: string;
  category: string;
  totalMeditationHours: number;
  is_free: boolean;
  price: number;
  audio_url?: string;
  background_music_url?: string;
}

interface UserCardInteraction {
  card_id: string;
  total_meditation_hours: number;
  timestamp: any; // Firestore Timestamp
  user_id: string;
  meditation_date: string;
}

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  error?: string;
}

interface PopularCard {
  name: string;
  count: number;
  meditationHours: number;
}

// Update the PopularCardsSection styled component
const PopularCardsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
  '& .card-item': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '@media (max-width: 1024px)': {
      padding: '16px',
    },
    '& .card-info': {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      '@media (max-width: 1024px)': {
        gap: '16px',
      },
      '& .card-name': {
        fontSize: '14px',
        fontWeight: 500,
        color: '#424242',
        '@media (max-width: 1024px)': {
          fontSize: '16px',
        }
      },
      '& .meditation-hours': {
        fontSize: '12px',
        color: '#757575',
        backgroundColor: '#f5f5f5',
        padding: '2px 8px',
        borderRadius: '12px',
        '@media (max-width: 1024px)': {
          fontSize: '14px',
          padding: '4px 12px',
        }
      }
    },
    '& .card-count': {
      fontSize: '14px',
      fontWeight: 500,
      color: '#424242',
      '@media (max-width: 1024px)': {
        fontSize: '16px',
      }
    }
  }
}));

export default function Dashboard() {
  const [timeFilter, setTimeFilter] = useState('Free');
  const [activeUsersFilter, setActiveUsersFilter] = useState('Current Week');
  const [activeUsersDateRange, setActiveUsersDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    error: ''
  });
  const [registeredUsersDateRange, setRegisteredUsersDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    error: ''
  });
  const [cards, setCards] = useState<YantraCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(3);
  const [activeUsers, setActiveUsers] = useState(0);
  const [registeredUsers, setRegisteredUsers] = useState(0);
  const [registeredUsersFilter, setRegisteredUsersFilter] = useState('Current Week');
  const [activeCardsCount, setActiveCardsCount] = useState(0);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});
  const [imageCache, setImageCache] = useState<{ [key: string]: string }>({});
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const navigate = useNavigate();
  const [orderByField, setOrderByField] = useState<keyof Track>('updated_at');
  const [noDataMessage, setNoDataMessage] = useState<{
    activeUsers: string;
    registrations: string;
  }>({
    activeUsers: '',
    registrations: ''
  });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    fetchCards();
  }, []);

  // Add useEffect for scroll to top
  useEffect(() => {
    const handleNavigation = () => {
      window.scrollTo(0, 0);
    };

    // Add event listener for navigation
    window.addEventListener('popstate', handleNavigation);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const { innerWidth: w, innerHeight: h } = window;
      if (w < 768) {
        setCardsPerPage(1);
      } else if (w >= 768 && w <= 1024) {
        // iPad specific logic
        if (w > h) { // Landscape mode
          setCardsPerPage(2);
        } else { // Portrait mode
          setCardsPerPage(1);
        }
      } else if (w > 1024) {
        setCardsPerPage(3);
      } else {
        setCardsPerPage(1);
      }
    };
    handleResize(); // Set on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCards = async () => {
    try {
      setLoading(true);
      
      // First, fetch all active cards
      const cardsRef = collection(db, 'cards');
      const cardsSnapshot = await getDocs(cardsRef);
      const activeCards = cardsSnapshot.docs
        .filter(doc => doc.data().is_active === true)
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as FirestoreCard));

      // Fetch all user card interactions
      const interactionsRef = collection(db, 'user_card_interactions');
      const interactionsSnapshot = await getDocs(interactionsRef);
      
      // Calculate total meditation hours for each card
      const cardHours: { [key: string]: number } = {};
      interactionsSnapshot.docs.forEach(doc => {
        const interaction = doc.data();
        
        // Extract card ID from the reference path
        let cardId = '';
        if (typeof interaction.card_id === 'string') {
          // If it's a reference path like "/cards/fOK3lVxh9FeScPrlqtPc"
          const parts = interaction.card_id.split('/');
          cardId = parts[parts.length - 1];
        } else if (interaction.card_id?.id) {
          // If it's a DocumentReference object
          cardId = interaction.card_id.id;
        }
        
        if (cardId && interaction.total_meditation_hours !== undefined) {
          const hours = Number(interaction.total_meditation_hours);
          if (!isNaN(hours)) {
            cardHours[cardId] = (cardHours[cardId] || 0) + hours;
          }
        }
      });

      // Map cards with their total meditation hours and sort by hours
      const cardsWithHours = activeCards.map(card => {
        const hours = cardHours[card.id] || 0;
        return {
          id: card.id,
          name: card.name || '',
          image_url: card.image_url || '',
          thumbnail_url: card.thumbnail_url || undefined,
          description: card.description || '',
          category: card.category || '',
          totalMeditationHours: hours,
          is_free: card.is_free || false,
          price: card.price || 0,
          audio_url: card.audio_url,
          background_music_url: card.background_music_url
        } as YantraCard;
      });

      // Filter out cards with zero hours and sort by total meditation hours (highest first)
      const popularCards = cardsWithHours
        .filter(card => card.totalMeditationHours > 0) // Only include cards with hours > 0
        .sort((a, b) => b.totalMeditationHours - a.totalMeditationHours)
        .slice(0, 9);

      setCards(popularCards);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching popular cards:', error);
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(cards.length / cardsPerPage) - 1));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const getCurrentPageCards = () => {
    const startIndex = currentPage * cardsPerPage;
    return cards.slice(startIndex, startIndex + cardsPerPage);
  };

  const handleActiveUsersFilterChange = (value: string) => {
    setActiveUsersFilter(value);
    if (value !== 'Custom Range') {
      setActiveUsersDateRange({ startDate: null, endDate: null, error: '' });
      fetchActiveUsers();
    }
  };

  const handleRegisteredUsersFilterChange = (value: string) => {
    setRegisteredUsersFilter(value);
    if (value !== 'Custom Range') {
      setRegisteredUsersDateRange({ startDate: null, endDate: null, error: '' });
      fetchRegisteredUsers();
    }
  };

  const formatDateToUS = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const handleDateChange = (type: 'start' | 'end', value: Date | null, isActiveUsers: boolean) => {
    const setDateRange = isActiveUsers ? setActiveUsersDateRange : setRegisteredUsersDateRange;
    const currentDateRange = isActiveUsers ? activeUsersDateRange : registeredUsersDateRange;
    
    if (value) {
      if (type === 'start') {
        // Check if there's an end date and the new start date is after it
        if (currentDateRange.endDate && value > currentDateRange.endDate) {
          setDateRange(prev => ({
            ...prev,
            startDate: value,
            error: 'End date cannot be before start date'
          }));
        } else {
          setDateRange(prev => ({
            ...prev,
            startDate: value,
            error: ''
          }));
        }
      } else {
        // For end date
        if (currentDateRange.startDate && value < currentDateRange.startDate) {
          setDateRange(prev => ({
            ...prev,
            endDate: value,
            error: 'End date cannot be before start date'
          }));
        } else {
          setDateRange(prev => ({
            ...prev,
            endDate: value,
            error: ''
          }));
        }
      }
    } else {
      // Handle null value (date cleared)
      setDateRange(prev => ({
        ...prev,
        [type === 'start' ? 'startDate' : 'endDate']: null,
        error: ''
      }));
    }
  };

  const handleDateRangeConfirm = (isActiveUsers: boolean) => {
    const dateRange = isActiveUsers ? activeUsersDateRange : registeredUsersDateRange;
    const setDateRange = isActiveUsers ? setActiveUsersDateRange : setRegisteredUsersDateRange;
    
    // Validate date selections
    if (!dateRange.startDate && !dateRange.endDate) {
      setDateRange(prev => ({
        ...prev,
        error: 'Please select a date range'
      }));
      return;
    }

    if (!dateRange.startDate) {
      setDateRange(prev => ({
        ...prev,
        error: 'Please select a start date'
      }));
      return;
    }

    if (!dateRange.endDate) {
      setDateRange(prev => ({
        ...prev,
        error: 'Please select an end date'
      }));
      return;
    }

    // Check if end date is before start date
    if (dateRange.endDate < dateRange.startDate) {
      setDateRange(prev => ({
        ...prev,
        error: 'End date cannot be before start date'
      }));
      return;
    }

    // Clear error if all validations pass
    setDateRange(prev => ({
      ...prev,
      error: ''
    }));

    // If all validations pass, fetch the data
    if (isActiveUsers) {
      if (activeUsersFilter === 'Custom Range') {
        fetchActiveUsers();
      }
    } else {
      if (registeredUsersFilter === 'Custom Range') {
        fetchRegisteredUsers();
      }
    }
  };

  const handleDateRangeCancel = (isActiveUsers: boolean) => {
    if (isActiveUsers) {
      setActiveUsersFilter('Current Week');
      setActiveUsersDateRange({ startDate: null, endDate: null, error: '' });
      fetchActiveUsers();
    } else {
      setRegisteredUsersFilter('Current Week');
      setRegisteredUsersDateRange({ startDate: null, endDate: null, error: '' });
      fetchRegisteredUsers();
    }
  };

  // Function to fetch active users count
  const fetchActiveUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const now = new Date();
      let startDate: Date;
      let endDate = now;

      switch (activeUsersFilter) {
        case 'Current Week':
          const currentDay = now.getDay();
          const diff = now.getDate() - currentDay;
          startDate = new Date(now.setDate(diff)); // Sunday
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6); // Saturday
          break;
        case 'Current Month':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        case 'Current Year':
          startDate = startOfYear(now);
          endDate = endOfYear(now);
          break;
        case 'Custom Range':
          if (activeUsersDateRange.startDate && activeUsersDateRange.endDate) {
            startDate = activeUsersDateRange.startDate;
            endDate = activeUsersDateRange.endDate;
          } else {
            const currentDay = now.getDay();
            const diff = now.getDate() - currentDay;
            startDate = new Date(now.setDate(diff)); // Sunday
            endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6); // Saturday
          }
          break;
        default:
          const day = now.getDay();
          const difference = now.getDate() - day;
          startDate = new Date(now.setDate(difference)); // Sunday
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6); // Saturday
      }

      // Count users who were active within the selected date range
      let activeCount = 0;
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        const lastActive = userData.last_active_on?.toDate();
        
        if (lastActive && isWithinInterval(lastActive, { start: startDate, end: endDate })) {
          activeCount++;
        }
      });

      // Set the active users count
      setActiveUsers(activeCount);

      // Set appropriate message based on the count
      if (activeCount === 0) {
        setNoDataMessage(prev => ({
          ...prev,
          activeUsers: `No active users found for the selected ${activeUsersFilter.toLowerCase()}`
        }));
      } else {
        setNoDataMessage(prev => ({
          ...prev,
          activeUsers: ''
        }));
      }

    } catch (error) {
      console.error('Error fetching active users:', error);
      setNoDataMessage(prev => ({
        ...prev,
        activeUsers: 'Error fetching active users data'
      }));
    }
  };

  // Function to fetch registered users count
  const fetchRegisteredUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      const now = new Date();
      let startDate: Date;
      let endDate = now;

      switch (registeredUsersFilter) {
        case 'Current Week':
          const currentDay = now.getDay();
          const diff = now.getDate() - currentDay;
          startDate = new Date(now.setDate(diff)); // Sunday
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6); // Saturday
          break;
        case 'Current Month':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        case 'Current Year':
          startDate = startOfYear(now);
          endDate = endOfYear(now);
          break;
        case 'Custom Range':
          if (registeredUsersDateRange.startDate && registeredUsersDateRange.endDate) {
            startDate = registeredUsersDateRange.startDate;
            endDate = registeredUsersDateRange.endDate;
          } else {
            const currentDay = now.getDay();
            const diff = now.getDate() - currentDay;
            startDate = new Date(now.setDate(diff)); // Sunday
            endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6); // Saturday
          }
          break;
        default:
          const day = now.getDay();
          const difference = now.getDate() - day;
          startDate = new Date(now.setDate(difference)); // Sunday
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6); // Saturday
      }

      // Count users who registered within the selected date range
      let registeredCount = 0;
      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        const createdTime = userData.created_time?.toDate();
        
        if (createdTime && isWithinInterval(createdTime, { start: startDate, end: endDate })) {
          registeredCount++;
        }
      });

      // Set the registered users count
      setRegisteredUsers(registeredCount);

      // Set appropriate message based on the count
      if (registeredCount === 0) {
        setNoDataMessage(prev => ({
          ...prev,
          registrations: `No registrations found for the selected ${registeredUsersFilter.toLowerCase()}`
        }));
      } else {
        setNoDataMessage(prev => ({
          ...prev,
          registrations: ''
        }));
      }

    } catch (error) {
      console.error('Error fetching registered users:', error);
      setNoDataMessage(prev => ({
        ...prev,
        registrations: 'Error fetching registration data'
      }));
    }
  };

  // Update the useEffect hooks to not trigger on dateRange changes
  useEffect(() => {
    if (activeUsersFilter !== 'Custom Range') {
      fetchActiveUsers();
    }
  }, [activeUsersFilter]);

  useEffect(() => {
    if (registeredUsersFilter !== 'Custom Range') {
      fetchRegisteredUsers();
    }
  }, [registeredUsersFilter]);

  // Add function to fetch active cards count
  const fetchActiveCardsCount = async (filter: string) => {
    try {
      const cardsRef = collection(db, 'cards');
      const cardsSnapshot = await getDocs(cardsRef);
      
      let count = 0;
      cardsSnapshot.docs.forEach(doc => {
        const cardData = doc.data();
        const price = cardData.price ? Number(cardData.price) : 0;
        const is_free = cardData.is_free !== undefined ? cardData.is_free : price === 0;
        const is_active = cardData.is_active !== undefined ? cardData.is_active : true;

        // Only count cards that are active and match the filter
        if (is_active) {
          if (filter === 'Free' && is_free) {
            count++;
          } else if (filter === 'Paid' && !is_free) {
            count++;
          }
        }
      });

      setActiveCardsCount(count);
    } catch (error) {
      console.error('Error fetching active cards count:', error);
    }
  };

  // Update useEffect to fetch active cards count when filter changes
  useEffect(() => {
    fetchActiveCardsCount(timeFilter);
  }, [timeFilter]);

  // Preload images for the current page
  useEffect(() => {
    const preloadImages = async () => {
      const currentCards = getCurrentPageCards();
      const imagePromises = currentCards.map(card => {
        if (!imageCache[card.id]) {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              // Store both thumbnail and main image URLs in cache
              setImageCache(prev => ({ 
                ...prev, 
                [card.id]: `${FIREBASE_STORAGE_URL}${card.thumbnail_url || card.image_url}` 
              }));
              setLoadedImages(prev => ({ ...prev, [card.id]: true }));
              resolve(null);
            };
            img.onerror = () => {
              console.error('Image failed to load for card:', {
                cardId: card.id,
                cardName: card.name,
                failedUrl: img.src
              });
              img.onerror = null; // Prevent infinite loop
              
              // If thumbnail failed, try the main image
              if (img.src === `${FIREBASE_STORAGE_URL}${card.thumbnail_url}`) {
                img.src = `${FIREBASE_STORAGE_URL}${card.image_url}`;
                return;
              }
              
              // If both failed, use fallback
              const imageFileName = IMAGE_NUMBER_MAP[card.name] || 
                                  IMAGE_NUMBER_MAP[card.name.replace(/\s+/g, '-')] || 
                                  '1-Thought';
              
                                      const fallbackUrl = `${FIREBASE_STORAGE_URL}Yantra_Images%2F${imageFileName}.jpg`;
              img.src = fallbackUrl;
            };
            // Try loading thumbnail first by adding base URL to stored path
            img.src = card.thumbnail_url || card.image_url ? `${FIREBASE_STORAGE_URL}${card.thumbnail_url || card.image_url}` : '';
          });
        }
        return Promise.resolve();
      });

      try {
        await Promise.all(imagePromises);
      } catch (error) {
        console.error('Error preloading images:', error);
      }
    };

    preloadImages();
  }, [currentPage, cards]);

  const stats = [
    {
      title: 'Active Users',
      value: activeUsers.toString(),
      filter: activeUsersFilter,
      onFilterChange: handleActiveUsersFilterChange,
      filterOptions: ['Current Week', 'Current Month', 'Current Year', 'Custom Range']
    },
    {
      title: 'Registrations',
      value: registeredUsers.toString(),
      filter: registeredUsersFilter,
      onFilterChange: handleRegisteredUsersFilterChange,
      filterOptions: ['Current Week', 'Current Month', 'Current Year', 'Custom Range']
    },
    {
      title: 'Active Yantra Cards',
      value: activeCardsCount.toString(),
      filter: timeFilter,
      onFilterChange: (value: string) => {
        setTimeFilter(value);
        fetchActiveCardsCount(value);
      },
      filterOptions: ['Free', 'Paid'],
      link: '/yantra-cards'
    },
  ];

  // Update the popularCards data
  const popularCards: PopularCard[] = [
    { name: 'Morning Meditation', count: 120, meditationHours: 2.5 },
    { name: 'Stress Relief', count: 95, meditationHours: 1.5 },
    { name: 'Sleep Better', count: 80, meditationHours: 3.0 },
    { name: 'Anxiety Relief', count: 65, meditationHours: 2.0 },
    { name: 'Focus & Concentration', count: 50, meditationHours: 1.0 }
  ];

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <MainContent>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#424242', fontWeight: 'bold', mb: 0.5, fontSize: '1.5rem' }}>
          Popular Cards
        </Typography>
        <Typography variant="body2" sx={{ color: '#757575' }}>
          Monitor the most popular Yantra cards currently engaging users
        </Typography>
      </Box>

      {/* Cards Slider Section */}
      {loading ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          <CircularProgress sx={{ color: '#FF7043' }} />
          <Typography>Loading cards...</Typography>
        </Box>
      ) : cards.length > 0 ? (
        <>
          <Grid container spacing={3} sx={{ 
            mb: 3,
            alignItems: 'center',
            justifyContent: 'center',
            '@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape)': {
              maxWidth: '900px',
              margin: '0 auto',
            },
          }}>
            {getCurrentPageCards().map((card) => (
              <Grid 
                item 
                xs={12} 
                md={4}
                // For tablets/iPads in landscape, show 2 cards per row
                sx={{
                  '@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape)': {
                    flexBasis: '50%',
                    maxWidth: '50%',
                  },
                  '@media (min-width: 1025px)': {
                    flexBasis: '33.3333%',
                    maxWidth: '33.3333%',
                  }
                }}
                key={card.id}
              >
                <PopularCard>
                  {!loadedImages[card.id] ? (
                    <ImagePlaceholder>
                      <CircularProgress size={40} sx={{ color: '#FF7043' }} />
                    </ImagePlaceholder>
                  ) : (
                    <CardImage 
                      src={imageCache[card.id] || (card.thumbnail_url || card.image_url ? `${FIREBASE_STORAGE_URL}${card.thumbnail_url || card.image_url}` : '')} 
                      alt={card.name}
                      loading="lazy"
                      style={{ opacity: loadedImages[card.id] ? 1 : 0 }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        console.error('Image failed to load for card:', {
                          cardId: card.id,
                          cardName: card.name,
                          failedUrl: target.src
                        });
                        target.onerror = null; // Prevent infinite loop
                        
                        // If thumbnail failed, try the main image
                        if (target.src === `${FIREBASE_STORAGE_URL}${card.thumbnail_url}`) {
                          target.src = `${FIREBASE_STORAGE_URL}${card.image_url}`;
                          return;
                        }
                        
                        // If both failed, use fallback
                        const imageFileName = IMAGE_NUMBER_MAP[card.name] || 
                                            IMAGE_NUMBER_MAP[card.name.replace(/\s+/g, '-')] || 
                                            '1-Thought';
                        
                        const fallbackUrl = `${FIREBASE_STORAGE_URL}Yantra_Images%2F${imageFileName}.jpg`;
                        target.src = fallbackUrl;
                      }}
                    />
                  )}
                  <CardContent sx={{ 
                    width: '100%', 
                    textAlign: 'center', 
                    padding: '8px !important',
                    backgroundColor: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    height: 'auto',
                    '& .card-info': {
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
                      justifyContent: { xs: 'center', sm: 'center', md: 'center', lg: 'space-between' },
                      width: '100%',
                      padding: '0 8px',
                      minHeight: '32px',
                      gap: 1,
                    }
                  }}>
                    <Box
                      className="card-info"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
                        justifyContent: { xs: 'center', sm: 'center', md: 'center', lg: 'space-between' },
                        width: '100%',
                        padding: '0 8px',
                        minHeight: '32px',
                        gap: 1,
                      }}
                    >
                      {isMdUp ? (
                        <Tooltip title={card.name} placement="top">
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: '#FF7043',
                              fontWeight: 'bold',
                              fontSize: { xs: '15px', sm: '16px', md: '18px' },
                              textAlign: 'center',
                              lineHeight: 1.2,
                              whiteSpace: { xs: 'normal', sm: 'normal', md: 'normal', lg: 'nowrap' },
                              overflow: { lg: 'hidden' },
                              textOverflow: { lg: 'ellipsis' },
                              maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '200px' },
                              minWidth: '0',
                              flex: 1,
                            }}
                          >
                            {card.name}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#FF7043',
                            fontWeight: 'bold',
                            fontSize: { xs: '15px', sm: '16px', md: '18px' },
                            textAlign: 'center',
                            lineHeight: 1.2,
                            maxWidth: '100%',
                            minWidth: '0',
                            flex: 1,
                          }}
                        >
                          {card.name}
                        </Typography>
                      )}
                      <MeditationHours sx={{ mt: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0 }, justifyContent: 'center' }}>
                        <AccessTimeIcon sx={{ fontSize: { xs: '16px', sm: '18px', md: '20px' } }} />
                        <Typography sx={{ fontSize: { xs: '13px', sm: '14px', md: '15px' }, textAlign: 'center' }}>
                          {formatTimeToHHMM(card.totalMeditationHours)} hrs
                        </Typography>
                      </MeditationHours>
                    </Box>
                    {card.category && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#757575',
                          display: 'block',
                          textAlign: 'left',
                          padding: '0 8px',
                          lineHeight: 1.2,
                          maxHeight: '16px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {card.category}
                      </Typography>
                    )}
                  </CardContent>
                </PopularCard>
              </Grid>
            ))}
          </Grid>

          {/* Navigation Controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 2,
            mb: 4 
          }}>
            <IconButton 
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              sx={{ 
                color: currentPage === 0 ? '#E0E0E0' : '#FF7043',
                backgroundColor: currentPage === 0 ? 'transparent' : 'rgba(255, 112, 67, 0.08)',
                cursor: 'pointer',
                padding: '12px',
                '&:hover': {
                  backgroundColor: currentPage === 0 ? 'transparent' : 'rgba(255, 112, 67, 0.15)'
                },
                '&.Mui-disabled': {
                  cursor: 'not-allowed'
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '32px'
                }
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            {/* Page Dots */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {Array.from({ length: Math.ceil(cards.length / cardsPerPage) }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: index === currentPage ? '#FF7043' : '#E0E0E0',
                    transition: 'all 0.3s ease',
                    transform: index === currentPage ? 'scale(1.2)' : 'scale(1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.2)',
                      bgcolor: index === currentPage ? '#FF7043' : '#BDBDBD'
                    }
                  }}
                  onClick={() => setCurrentPage(index)}
                />
              ))}
            </Box>

            <IconButton 
              onClick={handleNextPage}
              disabled={currentPage === Math.ceil(cards.length / cardsPerPage) - 1}
              sx={{ 
                color: currentPage === Math.ceil(cards.length / cardsPerPage) - 1 ? '#E0E0E0' : '#FF7043',
                backgroundColor: currentPage === Math.ceil(cards.length / cardsPerPage) - 1 ? 'transparent' : 'rgba(255, 112, 67, 0.08)',
                cursor: 'pointer',
                padding: '12px',
                '&:hover': {
                  backgroundColor: currentPage === Math.ceil(cards.length / cardsPerPage) - 1 ? 'transparent' : 'rgba(255, 112, 67, 0.15)'
                },
                '&.Mui-disabled': {
                  cursor: 'not-allowed'
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '32px'
                }
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        </>
      ) : (
        <EmptyState>
          <Typography variant="h6" sx={{ color: '#757575', mb: 1 }}>
            No Cards Available
          </Typography>
          <Typography variant="body2" sx={{ color: '#757575' }}>
            There are no meditation cards to display at the moment.
          </Typography>
        </EmptyState>
      )}

      {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
        {stats.map((stat, index) => (
            <Grid item xs={12} sm={12} md={6} lg={4} key={index} sx={{ mb: 4 }}>
            <StatsCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#000000', fontWeight: 'bold' }}>
                  {stat.title}
                </Typography>
                <FormControl size="small">
                  <Select
                    value={stat.filter}
                    sx={{ 
                      minWidth: 120,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      '& .MuiSelect-select': {
                        padding: '4px 8px',
                      }
                    }}
                    onChange={(e: SelectChangeEvent<string>) => stat.onFilterChange(e.target.value)}
                  >
                    {stat.filterOptions.map((option) => (
                      <MenuItem key={option} value={option} sx={{ cursor: 'pointer' }}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                {stat.value}
              </Typography>
              {stat.title === 'Active Users' && noDataMessage.activeUsers && (
                <Typography variant="body2" sx={{ color: '#757575', fontStyle: 'italic' }}>
                  {noDataMessage.activeUsers}
                </Typography>
              )}
              {stat.title === 'Registrations' && noDataMessage.registrations && (
                <Typography variant="body2" sx={{ color: '#757575', fontStyle: 'italic' }}>
                  {noDataMessage.registrations}
                </Typography>
              )}
              {stat.link && (
                <Typography 
                  variant="caption" 
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate(stat.link);
                  }}
                  sx={{ 
                    color: '#FF7043',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Manage Cards
                  <NavigateNextIcon sx={{ fontSize: '16px' }} />
                </Typography>
              )}
              {((stat.title === 'Active Users' && stat.filter === 'Custom Range') || 
                (stat.title === 'Registrations' && stat.filter === 'Custom Range')) && (
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <DatePicker
                        selected={stat.title === 'Active Users' 
                          ? activeUsersDateRange.startDate 
                          : registeredUsersDateRange.startDate}
                        onChange={(date: Date | null) => {
                          handleDateChange('start', date, stat.title === 'Active Users');
                        }}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="Start Date"
                        className="custom-datepicker"
                        wrapperClassName="datepicker-wrapper"
                        popperProps={{ strategy: 'fixed' }}
                        customInput={
                          <TextField
                            size="small"
                            fullWidth
                            inputProps={{
                              style: {
                                fontSize: '14px',
                                paddingLeft: '8px',
                                paddingRight: '0px',
                                letterSpacing: '0.05em',
                                height: 'auto',
                              }
                            }}
                            error={Boolean(stat.title === 'Active Users' 
                              ? activeUsersDateRange.error 
                              : registeredUsersDateRange.error)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ pr: 0.3 }}>
                                  <CalendarTodayIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-input': {
                                paddingRight: '0px',
                                paddingLeft: '8px',
                                cursor: 'pointer',
                              },
                              '& .MuiOutlinedInput-root': {
                                paddingRight: '5px',
                                '& fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                              },
                            }}
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DatePicker
                        selected={stat.title === 'Active Users' 
                          ? activeUsersDateRange.endDate 
                          : registeredUsersDateRange.endDate}
                        onChange={(date: Date | null) => {
                          handleDateChange('end', date, stat.title === 'Active Users');
                        }}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="End Date"
                        className="custom-datepicker"
                        wrapperClassName="datepicker-wrapper"
                        popperProps={{ strategy: 'fixed' }}
                        customInput={
                          <TextField
                            size="small"
                            fullWidth
                            inputProps={{
                              style: {
                                fontSize: '14px',
                                paddingLeft: '8px',
                                paddingRight: '0px',
                                letterSpacing: '0.05em',
                                height: 'auto',
                              }
                            }}
                            error={Boolean(stat.title === 'Active Users' 
                              ? activeUsersDateRange.error 
                              : registeredUsersDateRange.error)}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ pr: 0.3 }}>
                                  <CalendarTodayIcon fontSize="small" sx={{ cursor: 'pointer' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              cursor: 'pointer',
                              '& .MuiOutlinedInput-input': {
                                paddingRight: '0px',
                                paddingLeft: '8px',
                                cursor: 'pointer',
                              },
                              '& .MuiOutlinedInput-root': {
                                paddingRight: '5px',
                                '& fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: 'rgba(0, 0, 0, 0.23)',
                                },
                              },
                            }}
                          />
                        }
                      />
                    </Grid>
                    {(stat.title === 'Active Users' ? activeUsersDateRange.error : registeredUsersDateRange.error) && (
                      <Grid item xs={12}>
                        <Box sx={{ color: 'error.main', fontSize: '0.85rem', mt: 1, mb: 1, textAlign: 'left' }}>
                          {stat.title === 'Active Users' ? activeUsersDateRange.error : registeredUsersDateRange.error}
                        </Box>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => handleDateRangeCancel(stat.title === 'Active Users')}
                          sx={{ 
                            color: '#757575',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleDateRangeConfirm(stat.title === 'Active Users')}
                          sx={{ 
                            bgcolor: '#FF7043',
                            color: 'white',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: '#F4511E'
                            }
                          }}
                        >
                          Okay
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </StatsCard>
          </Grid>
        ))}
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          },
          '& .MuiSnackbarContent-root': {
            minWidth: 'auto',
            backgroundColor: '#FF7043',
            color: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
        }}
      >
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </MainContent>
  );
}